import React, { useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
function Modal({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) {
  if (!type) return null;
  const content = {
    privacy: {
      title: 'Política de Privacidad',
      body: <div className="space-y-6 text-charcoal-light text-sm leading-relaxed" data-id="element-14">
          <p data-id="element-15">
            En{' '}
            <strong className="text-charcoal" data-id="element-16">
              Protocolo Vientre Plano 21D
            </strong>
            , nos comprometemos a proteger tu privacidad. Esta política describe
            cómo recopilamos, usamos y protegemos tu información personal.
          </p>

          <div data-id="element-17">
            <h3 className="font-serif font-bold text-charcoal text-base mb-2" data-id="element-18">
              1. Información que recopilamos
            </h3>
            <p data-id="element-19">
              Recopilamos únicamente la información necesaria para procesar tu
              compra y entregarte el producto digital. Esto puede incluir tu
              nombre, dirección de correo electrónico y datos de pago
              (procesados de forma segura por nuestro proveedor de pagos).
            </p>
          </div>

          <div data-id="element-20">
            <h3 className="font-serif font-bold text-charcoal text-base mb-2" data-id="element-21">
              2. Uso de la información
            </h3>
            <p data-id="element-22">Utilizamos tu información exclusivamente para:</p>
            <ul className="list-disc list-inside mt-2 space-y-1" data-id="element-23">
              <li data-id="element-24">Procesar y confirmar tu compra</li>
              <li data-id="element-25">Entregarte acceso al producto digital</li>
              <li data-id="element-26">Enviarte actualizaciones del producto que adquiriste</li>
              <li data-id="element-27">Responder a solicitudes de soporte o reembolso</li>
            </ul>
          </div>

          <div data-id="element-28">
            <h3 className="font-serif font-bold text-charcoal text-base mb-2" data-id="element-29">
              3. Cookies
            </h3>
            <p data-id="element-30">
              Este sitio puede utilizar cookies técnicas y de análisis para
              mejorar la experiencia de navegación. No utilizamos cookies para
              rastrear tu actividad fuera de este sitio. Puedes desactivar las
              cookies desde la configuración de tu navegador.
            </p>
          </div>

          <div data-id="element-31">
            <h3 className="font-serif font-bold text-charcoal text-base mb-2" data-id="element-32">
              4. Compartir información con terceros
            </h3>
            <p data-id="element-33">
              No vendemos, alquilamos ni compartimos tu información personal con
              terceros, salvo con los proveedores de servicios necesarios para
              procesar pagos y entregar el producto (por ejemplo, plataformas de
              pago como Stripe o PayPal).
            </p>
          </div>

          <div data-id="element-34">
            <h3 className="font-serif font-bold text-charcoal text-base mb-2" data-id="element-35">
              5. Seguridad
            </h3>
            <p data-id="element-36">
              Implementamos medidas de seguridad estándar de la industria,
              incluyendo encriptación SSL, para proteger tu información durante
              la transmisión y el almacenamiento.
            </p>
          </div>

          <div data-id="element-37">
            <h3 className="font-serif font-bold text-charcoal text-base mb-2" data-id="element-38">
              6. Tus derechos
            </h3>
            <p data-id="element-39">
              Tienes derecho a acceder, corregir o solicitar la eliminación de
              tus datos personales en cualquier momento. Para ejercer estos
              derechos, puedes enviarnos una solicitud a través de los medios
              disponibles en la plataforma de compra.
            </p>
          </div>

          <div data-id="element-40">
            <h3 className="font-serif font-bold text-charcoal text-base mb-2" data-id="element-41">
              7. Cambios a esta política
            </h3>
            <p data-id="element-42">
              Nos reservamos el derecho de actualizar esta política en cualquier
              momento. Los cambios serán publicados en esta página con la fecha
              de actualización correspondiente.
            </p>
          </div>

          <p className="text-xs text-gray-400 pt-4 border-t border-beige-100" data-id="element-43">
            Última actualización: {new Date().getFullYear()}
          </p>
        </div>
    },
    terms: {
      title: 'Términos y Condiciones',
      body: <div className="space-y-6 text-charcoal-light text-sm leading-relaxed" data-id="element-44">
          <p data-id="element-45">
            Al adquirir el{' '}
            <strong className="text-charcoal" data-id="element-46">
              Protocolo Vientre Plano 21D
            </strong>
            , aceptas los siguientes términos y condiciones. Por favor, léelos
            detenidamente antes de realizar tu compra.
          </p>

          <div data-id="element-47">
            <h3 className="font-serif font-bold text-charcoal text-base mb-2" data-id="element-48">
              1. Naturaleza del producto
            </h3>
            <p data-id="element-49">
              Este es un producto digital de descarga inmediata. Una vez
              completada la compra, recibirás acceso instantáneo al contenido.
              Por la naturaleza digital del producto, no se realizan envíos
              físicos.
            </p>
          </div>

          <div data-id="element-50">
            <h3 className="font-serif font-bold text-charcoal text-base mb-2" data-id="element-51">
              2. Política de reembolso
            </h3>
            <p data-id="element-52">
              Ofrecemos una garantía de satisfacción de{' '}
              <strong className="text-charcoal" data-id="element-53">30 días</strong>. Si por
              cualquier razón no estás satisfecha con el producto, puedes
              solicitar un reembolso completo dentro de los 30 días posteriores
              a tu compra. El reembolso se procesará sin preguntas.
            </p>
          </div>

          <div data-id="element-54">
            <h3 className="font-serif font-bold text-charcoal text-base mb-2" data-id="element-55">
              3. Uso permitido
            </h3>
            <p data-id="element-56">
              El contenido adquirido es para uso personal exclusivo. Queda
              estrictamente prohibido:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1" data-id="element-57">
              <li data-id="element-58">Reproducir, distribuir o revender el contenido</li>
              <li data-id="element-59">Compartir credenciales de acceso con terceros</li>
              <li data-id="element-60">
                Utilizar el contenido con fines comerciales sin autorización
              </li>
            </ul>
          </div>

          <div data-id="element-61">
            <h3 className="font-serif font-bold text-charcoal text-base mb-2" data-id="element-62">
              4. Descargo de responsabilidad médica
            </h3>
            <p data-id="element-63">
              El contenido de este programa es únicamente informativo y
              educativo. No constituye consejo médico, diagnóstico ni
              tratamiento. Siempre consulta con un profesional de la salud antes
              de iniciar cualquier programa de alimentación o ejercicio,
              especialmente si tienes condiciones médicas preexistentes.
            </p>
          </div>

          <div data-id="element-64">
            <h3 className="font-serif font-bold text-charcoal text-base mb-2" data-id="element-65">
              5. Resultados
            </h3>
            <p data-id="element-66">
              Los resultados mostrados en este sitio son ejemplos reales pero no
              típicos. Los resultados individuales varían según factores como el
              estado de salud, la constancia y el estilo de vida de cada
              persona. No garantizamos resultados específicos.
            </p>
          </div>

          <div data-id="element-67">
            <h3 className="font-serif font-bold text-charcoal text-base mb-2" data-id="element-68">
              6. Propiedad intelectual
            </h3>
            <p data-id="element-69">
              Todo el contenido del Protocolo Vientre Plano 21D, incluyendo
              textos, imágenes, diseños y materiales, está protegido por
              derechos de autor. Todos los derechos reservados.
            </p>
          </div>

          <div data-id="element-70">
            <h3 className="font-serif font-bold text-charcoal text-base mb-2" data-id="element-71">
              7. Modificaciones
            </h3>
            <p data-id="element-72">
              Nos reservamos el derecho de modificar estos términos en cualquier
              momento. El uso continuado del producto implica la aceptación de
              los términos vigentes.
            </p>
          </div>

          <p className="text-xs text-gray-400 pt-4 border-t border-beige-100" data-id="element-73">
            Última actualización: {new Date().getFullYear()}
          </p>
        </div>
    }
  };
  const {
    title,
    body
  } = content[type];
  return <AnimatePresence data-id="element-74">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-75">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-76" />

        {/* Modal */}
        <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-77">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-6 border-b border-beige-100 flex-shrink-0" data-id="element-78">
            <h2 className="text-2xl font-serif font-bold text-charcoal" data-id="element-79">
              {title}
            </h2>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-sage-50 hover:bg-sage-100 flex items-center justify-center transition-colors" aria-label="Cerrar" data-id="element-80">
              <X className="w-5 h-5 text-sage-600" data-id="element-81" />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto px-8 py-6 flex-1" data-id="element-82">{body}</div>

          {/* Footer */}
          <div className="px-8 py-4 border-t border-beige-100 flex-shrink-0 bg-sage-50" data-id="element-83">
            <button onClick={onClose} className="w-full py-3 bg-sage-600 hover:bg-sage-700 text-white font-bold rounded-full transition-colors text-sm" data-id="element-84">
              Entendido
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
export function FooterSection() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-charcoal text-white py-12" data-id="element-85">
        <div className="container mx-auto px-4 text-center" data-id="element-86">
          <h3 className="text-2xl font-serif font-bold mb-4" data-id="element-87">
            Abdomen Plano 21 Días
          </h3>
          <p className="text-gray-400 text-sm mb-8 max-w-md mx-auto" data-id="element-88">
            Un enfoque holístico y científico para recuperar tu bienestar,
            reducir la inflamación y equilibrar tus hormonas.
          </p>
          <div className="flex justify-center gap-6 text-sm text-gray-500" data-id="element-89">
            <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors" data-id="element-90">
              Términos
            </button>
            <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors" data-id="element-91">
              Privacidad
            </button>
          </div>
          <p className="mt-8 text-xs text-gray-600" data-id="element-92">
            © 2026 Protocolo Abdomen Plano 21D. Todos los derechos
            reservados.Este sitio no es parte del sitio web de Facebook o
            Facebook Inc.
          </p>
        </div>
      </footer>

      <Modal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-93" />
    </>;
}